/**
 * The profit-and-loss statement: calendar quarters or years, each on both an accrual and a cash basis,
 * with where the revenue came from, who it came from, who earned it, and what was spent.
 *
 * The port of `Views/ProfitLossPage.xaml` + `ProfitLossViewModel`. The server aggregates
 * (`ProfitLossBuilder`); this page picks the grouping and range and lays out the answer. How a period
 * reads - its lines, its headline, the order periods appear in - is `profitloss.ts`, tested on its own.
 *
 * Things a reader has to be told, said where they look:
 *
 *   * PERIODS ARE UTC CALENDAR PERIODS, and a requested range is snapped outward to whole ones by the
 *     server - half a quarter cannot be compared with the quarter before it. The line under the title
 *     states the range the server actually used, not the one typed.
 *   * THE PER-CUSTOMER TABLE IS NOT A PARTITION OF NET PROFIT. Overhead belongs to no customer, so it
 *     is in the headline and in no row; summing the column and expecting net profit is the one mistake
 *     to avoid.
 *   * Losses read as ($1,234.56), the accountant's convention, on this page only.
 */
import type { Container } from 'webwin';
import * as crm from '../api/crm.js';
import { describeError } from '../api/http.js';
import {
  ProfitLossGrouping,
  type PlCustomerRow,
  type PlExpenseCategoryRow,
  type PlRevenueTypeRow,
  type PlTechnicianRow,
  type ProfitLossPeriodDto,
  type ProfitLossReportDto
} from '../contracts.gen.js';
import { download, openInNewTab } from '../download.js';
import { accountingMoney, headline, isEmptyReport, orderedPeriods, statementLines, type StatementLine } from '../profitloss.js';
import { isoDayOf, todayIso } from '../recurrence.js';
import type { Card } from '../ui/Card.js';
import { chip } from '../ui/Chip.js';
import { dateBox } from '../ui/DateBox.js';
import { EmptyState } from '../ui/EmptyState.js';
import { ReportTable } from '../ui/ReportTable.js';
import { StatusLine } from '../ui/StatusLine.js';
import {
  button,
  buttonRow,
  card,
  field,
  FONT,
  GAP,
  h1,
  h2,
  muted,
  relayout,
  scrollPage,
  stackOf,
  text,
  vstack
} from '../ui/layout.js';
import type { CrmPage, PageContext } from './page.js';

function pct(value: number): string {
  return `${(Math.round(value * 10) / 10).toString()}%`;
}

function hours(value: number): string {
  return (Math.round(value * 10) / 10).toString();
}

/**
 * UTC midnight of a day, plus `days` for the exclusive upper bound.
 *
 * NOT LOCAL MIDNIGHT, unlike every other range on the other pages. The statement's periods are UTC
 * calendar periods and the server snaps a range OUTWARD to whole ones, so a local-midnight end for
 * 31 December - 06:00Z on 1 January in Central time - lands inside the next year and pulls the whole
 * of it in: "Last year" showed two years. Found by clicking it.
 */
function utcMidnight(iso: string, days = 0): string | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + days)).toISOString() : undefined;
}

/** The server's exclusive end, as the last day it includes. */
function lastDayOf(exclusiveEndUtc: string): string {
  const end = new Date(exclusiveEndUtc);
  return Number.isNaN(end.getTime()) ? '' : isoDayOf(new Date(end.getTime() - 86_400_000).toISOString());
}

export function profitLossPage(): CrmPage {
  return {
    mount(host: Container, ctx: PageContext) {
      let grouping = ProfitLossGrouping.Quarter;

      const status = new StatusLine();
      const rangeLine = muted('');

      // =================================================================
      //  Controls
      // =================================================================

      const from = dateBox();
      const to = dateBox();
      const quarterly = button('Quarterly', () => void setGrouping(ProfitLossGrouping.Quarter), true);
      const yearly = button('Yearly', () => void setGrouping(ProfitLossGrouping.Year));

      const header = stackOf(
        vstack({ spacing: GAP.sm }),
        h1('Profit & Loss'),
        rangeLine,
        stackOf(buttonRow(), quarterly, yearly),
        stackOf(
          buttonRow({ spacing: GAP.md, align: 'end' }),
          field('From', from.picker, { width: 150 }),
          field('To', to.picker, { width: 150 }),
          button('Refresh', () => void load(), true)
        ),
        stackOf(
          buttonRow(),
          button('This year', () => void setRange(`${new Date().getFullYear()}-01-01`, todayIso())),
          button('Last year', () => {
            const y = new Date().getFullYear() - 1;
            void setRange(`${y}-01-01`, `${y}-12-31`);
          }),
          button('Default range', () => void setRange('', ''))
        ),
        stackOf(buttonRow(), button('PDF', () => exportPdf()), button('CSV', () => void exportCsv())),
        muted(
          'Revenue is net of sales tax. Accrual counts a figure in the period it was billed or incurred; cash counts it when the money moved. Technician wages reach the statement as payroll expense, not as cost of sales, unless an expense is flagged as a direct cost.'
        ),
        status.label
      );

      const empty = new EmptyState(
        'No invoiced revenue and no expenses in this range. Issue an invoice, or record what the business spends on the Expenses page, and the statement fills in.'
      );
      const periodsHost = vstack({ spacing: GAP.lg });

      const { view, content } = scrollPage();
      content.controls.add(stackOf(vstack({ spacing: GAP.lg }), header, empty.panel, periodsHost));
      host.controls.add(view);

      // =================================================================
      //  One period
      // =================================================================

      function buildPeriod(period: ProfitLossPeriodDto, isTotal: boolean): Card {
        const loss = period.accrual.netProfit < 0;
        const titleRow = buttonRow({ spacing: GAP.sm });
        // The server already labels the total period "Total".
        titleRow.controls.add(h2(period.label));
        if (loss) titleRow.controls.add(chip('loss', 'danger'));

        const statement = new ReportTable<StatementLine>({
          header: 'Line',
          name: l => l.label,
          strong: l => l.emphasis,
          columns: [
            { header: 'Accrual', width: 120, cell: l => l.accrual, role: l => (l.accrual.startsWith('(') ? 'danger' : 'text') },
            { header: 'Cash', width: 120, cell: l => l.cash, role: l => (l.cash.startsWith('(') ? 'danger' : 'text') }
          ]
        });
        statement.set(statementLines(period));

        const body = stackOf(
          vstack({ spacing: GAP.md }),
          titleRow,
          text(headline(period), { font: FONT.bold, role: loss ? 'danger' : 'text' }),
          statement.panel
        );

        if (period.byRevenueType.length > 0) {
          const t = new ReportTable<PlRevenueTypeRow>({
            header: 'Type',
            name: r => r.type,
            columns: [
              { header: 'Revenue', width: 100, cell: r => accountingMoney(r.revenue) },
              { header: 'Cost', width: 100, cell: r => accountingMoney(r.cost) },
              { header: 'Gross', width: 100, cell: r => accountingMoney(r.grossProfit), role: r => (r.grossProfit < 0 ? 'danger' : 'text') },
              { header: 'Margin', width: 70, cell: r => pct(r.marginPercent) },
              { header: 'Share', width: 70, cell: r => pct(r.sharePercent) }
            ]
          });
          t.set(period.byRevenueType);
          body.controls.add(muted('Where the revenue came from', 'subtle'));
          body.controls.add(t.panel);
        }

        if (period.byCustomer.length > 0) {
          const t = new ReportTable<PlCustomerRow>({
            header: 'Customer',
            name: r => r.customerName,
            columns: [
              { header: 'Revenue', width: 100, cell: r => accountingMoney(r.revenue) },
              { header: 'Parts', width: 95, cell: r => accountingMoney(r.partsCost) },
              { header: 'Booked spend', width: 105, cell: r => accountingMoney(r.attributedExpenses) },
              { header: 'Gross', width: 100, cell: r => accountingMoney(r.grossProfit), role: r => (r.grossProfit < 0 ? 'danger' : 'text') },
              { header: 'Margin', width: 70, cell: r => pct(r.marginPercent) }
            ]
          });
          t.set(period.byCustomer);
          body.controls.add(
            muted(
              'By customer - revenue, parts cost, spend booked to them, gross profit. Overhead belongs to no customer, so these rows do not add up to net profit.',
              'subtle'
            )
          );
          body.controls.add(t.panel);
        }

        if (period.byTechnician.length > 0) {
          const t = new ReportTable<PlTechnicianRow>({
            header: 'Technician',
            name: r => r.userName,
            columns: [
              { header: 'Billed h', width: 75, cell: r => hours(r.billedHours) },
              { header: 'Billed', width: 100, cell: r => accountingMoney(r.billedValue) },
              { header: 'Unbilled h', width: 80, cell: r => hours(r.unbilledHours) },
              { header: 'Unbilled', width: 100, cell: r => accountingMoney(r.unbilledValue) },
              { header: 'Rate', width: 90, cell: r => accountingMoney(r.effectiveHourlyRate) }
            ]
          });
          t.set(period.byTechnician);
          body.controls.add(muted('By technician - billed hours and value, then work logged but not yet invoiced', 'subtle'));
          body.controls.add(t.panel);
        }

        if (period.byExpenseCategory.length > 0) {
          const t = new ReportTable<PlExpenseCategoryRow>({
            header: 'Category',
            name: r => r.categoryName,
            columns: [
              { header: 'Accrual', width: 100, cell: r => accountingMoney(r.accrual) },
              { header: 'Cash', width: 100, cell: r => accountingMoney(r.cash) },
              { header: 'Direct', width: 95, cell: r => accountingMoney(r.directPortion) },
              { header: 'Count', width: 55, cell: r => String(r.count) },
              { header: 'Share', width: 70, cell: r => pct(r.sharePercent) }
            ]
          });
          t.set(period.byExpenseCategory);
          body.controls.add(muted('Expenses by category - accrual, cash, and the part that is a direct cost', 'subtle'));
          body.controls.add(t.panel);
        }

        const shell = card({ tint: isTotal ? 'alt' : 'card' });
        shell.controls.add(body);
        return shell;
      }

      // =================================================================
      //  Loading
      // =================================================================

      function render(report: ProfitLossReportDto): void {
        const label = report.grouping === ProfitLossGrouping.Year ? 'Yearly' : 'Quarterly';
        rangeLine.text = `${isoDayOf(report.fromUtc)} to ${lastDayOf(report.toUtc)} · ${label} · UTC calendar periods`;
        periodsHost.controls.clear();
        for (const { period, isTotal } of orderedPeriods(report)) periodsHost.controls.add(buildPeriod(period, isTotal));
        empty.update(isEmptyReport(report) ? 0 : 1);
        relayout(periodsHost);
      }

      function bounds(): { fromUtc?: string; toUtc?: string } {
        return { fromUtc: utcMidnight(from.iso()), toUtc: utcMidnight(to.iso(), 1) };
      }

      async function load(): Promise<void> {
        status.busy('Loading…');
        try {
          const { fromUtc, toUtc } = bounds();
          const report = await crm.getProfitLoss(grouping, fromUtc, toUtc, ctx.dispose.signal());
          if (ctx.cancelled()) return;
          status.clear();
          render(report);
        } catch (error) {
          if (!ctx.cancelled()) status.error('Could not load the profit and loss statement: ' + describeError(error));
        }
      }

      async function setGrouping(next: ProfitLossGrouping): Promise<void> {
        grouping = next;
        // The selected grouping is the primary (filled) button, so the choice in force is visible.
        quarterly.isDefault = next === ProfitLossGrouping.Quarter;
        yearly.isDefault = next === ProfitLossGrouping.Year;
        quarterly.invalidate();
        yearly.invalidate();
        await load();
      }

      async function setRange(fromIso: string, toIso: string): Promise<void> {
        from.set(fromIso || null);
        to.set(toIso || null);
        await load();
      }

      /** Called straight from the click: the new tab needs the click's user activation. */
      function exportPdf(): void {
        const { fromUtc, toUtc } = bounds();
        openInNewTab(crm.profitLossPdfPath(grouping, fromUtc, toUtc), 'JetsonCRM-ProfitLoss.pdf').catch(error => {
          if (!ctx.cancelled()) status.error('Could not open the PDF: ' + describeError(error));
        });
      }

      async function exportCsv(): Promise<void> {
        const { fromUtc, toUtc } = bounds();
        status.busy('Preparing the CSV…');
        try {
          await download(crm.profitLossCsvPath(grouping, fromUtc, toUtc), 'JetsonCRM-ProfitLoss.csv');
          if (!ctx.cancelled()) status.ok('CSV downloaded.');
        } catch (error) {
          if (!ctx.cancelled()) status.error('Could not download the CSV: ' + describeError(error));
        }
      }

      void load();
    }
  };
}
